import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  CalendarOff, CalendarPlus, Clock, SlidersHorizontal, AlertTriangle, Plus,
  Trash2, MapPin, Check, RefreshCw, Info, CalendarX, CalendarClock,
} from 'lucide-react';
import supabaseService from '../services/supabaseService';
import '../assets/styles/availability.css';

/* ------------------------------------------------------------------ constants */

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const DAY_PLURAL = ['Sundays', 'Mondays', 'Tuesdays', 'Wednesdays', 'Thursdays', 'Fridays', 'Saturdays'];

// Monday first. The database numbers weekdays 0 = Sunday (extract(dow)), but a
// week that starts on Sunday reads wrong for someone looking at their work week.
const WEEK_ORDER = [1, 2, 3, 4, 5, 6, 0];

const SECTIONS = [
  { key: 'timeoff', label: 'Time off', icon: CalendarOff },
  { key: 'exceptions', label: 'One-off days', icon: CalendarPlus },
  { key: 'hours', label: 'Weekly hours', icon: Clock },
  { key: 'rules', label: 'Booking rules', icon: SlidersHorizontal },
];

const SLOT_INTERVALS = [5, 10, 15, 20, 30, 60];
const BUFFERS = [0, 5, 10, 15, 20, 30, 45, 60];

/* ---------------------------------------------------------------- date + time */

const pad = (n) => String(n).padStart(2, '0');

// Local date parts, never toISOString(). NZ is UTC+12/13, so a UTC date string
// is yesterday's date for the whole first half of Viktoria's working day.
const toISODate = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

const addDays = (d, n) => {
  const copy = new Date(d.getTime());
  copy.setDate(copy.getDate() + n);
  return copy;
};

const parseDate = (value) => {
  if (!value) return null;
  const d = new Date(`${String(value).slice(0, 10)}T00:00:00`);
  return isNaN(d.getTime()) ? null : d;
};

const formatDate = (value) => {
  const d = parseDate(value);
  if (!d) return '—';
  return d
    .toLocaleDateString('en-NZ', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })
    .replace(/,/g, '');
};

const formatDateNoYear = (value) => {
  const d = parseDate(value);
  if (!d) return '—';
  return d.toLocaleDateString('en-NZ', { weekday: 'short', day: 'numeric', month: 'short' }).replace(/,/g, '');
};

const hhmm = (value) => String(value || '').slice(0, 5);

const formatTime = (value) => {
  const v = hhmm(value);
  if (!v) return '';
  const [h, m] = v.split(':');
  const hour = parseInt(h, 10);
  if (isNaN(hour)) return v;
  return `${hour % 12 || 12}:${(m || '00').slice(0, 2)}${hour >= 12 ? 'pm' : 'am'}`;
};

const timeToMinutes = (value) => {
  const [h, m] = hhmm(value).split(':').map(Number);
  return Number.isFinite(h) ? h * 60 + (Number.isFinite(m) ? m : 0) : null;
};

const formatDuration = (mins) => {
  const n = Number(mins);
  if (!Number.isFinite(n) || n <= 0) return '';
  if (n < 60) return `${n} min`;
  const h = Math.floor(n / 60);
  const m = n % 60;
  return m ? `${h}h ${m}m` : `${h}h`;
};

// A whole-number day index built only from the date's own parts, so nothing here
// ever consults the machine's timezone. Only differences between two of these
// are ever used, so the epoch it counts from does not matter.
const dayNumber = (isoDate) => {
  const [y, m, d] = String(isoDate).slice(0, 10).split('-').map(Number);
  if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) return 0;
  return Math.round(Date.UTC(y, m - 1, d) / 86400000);
};

const nextISODate = (isoDate) => toISODate(addDays(parseDate(isoDate) || new Date(), 1));

// time_off timestamps are NZ wall clock with no zone ("2026-08-14T14:00:00").
// They are split as text on purpose — handing one to new Date() would have the
// browser read it as local or UTC depending on the shape and shift it by half a day.
const splitStamp = (stamp) => {
  const s = String(stamp || '').replace(' ', 'T');
  const [date, time = '00:00'] = s.split('T');
  return { date: date.slice(0, 10), time: time.slice(0, 5) };
};

const stampMinutes = ({ date, time }) => dayNumber(date) * 1440 + (timeToMinutes(time) || 0);

const nowMinutes = () => {
  const now = new Date();
  return dayNumber(toISODate(now)) * 1440 + now.getHours() * 60 + now.getMinutes();
};

const formatNowPlusHours = (hours) => {
  const d = new Date(Date.now() + hours * 3600000);
  return `${formatDate(toISODate(d))} at ${formatTime(`${pad(d.getHours())}:${pad(d.getMinutes())}`)}`;
};

/* --------------------------------------------------------------------- labels */

const timeOffLabel = (row) => {
  const s = splitStamp(row.start_at);
  const e = splitStamp(row.end_at);
  // Whole-day blocks are written as 00:00 → 23:59, so they are recognised by
  // shape rather than by a column the table does not have.
  const wholeDays = s.time === '00:00' && e.time === '23:59';

  if (wholeDays) {
    if (s.date === e.date) return { main: formatDate(s.date), sub: 'Blocked all day' };
    const span = dayNumber(e.date) - dayNumber(s.date) + 1;
    return {
      main: `${formatDateNoYear(s.date)} – ${formatDate(e.date)}`,
      sub: `${span} whole days blocked`,
    };
  }
  if (s.date === e.date) {
    return { main: formatDate(s.date), sub: `${formatTime(s.time)} – ${formatTime(e.time)}` };
  }
  return {
    main: `${formatDateNoYear(s.date)} ${formatTime(s.time)} – ${formatDate(e.date)} ${formatTime(e.time)}`,
    sub: 'Runs across more than one day',
  };
};

const timeOffState = (row) => {
  const start = stampMinutes(splitStamp(row.start_at));
  const end = stampMinutes(splitStamp(row.end_at));
  const now = nowMinutes();
  if (now >= end) return { cls: 'past', label: 'Finished' };
  if (now >= start) return { cls: 'now', label: 'On now' };
  return { cls: 'upcoming', label: 'Upcoming' };
};

const treatmentNames = (booking) =>
  (booking.booking_treatments || [])
    .map((t) => t.treatment_name)
    .filter(Boolean)
    .join(', ');

// Times are left exactly as stored, empty included. A day that has never had
// hours saved must render as an empty field, not as a plausible 09:00–17:00 —
// on screen a made-up default and a real saved value are indistinguishable.
const buildHoursDraft = (rows) => {
  const draft = {};
  (rows || []).forEach((row) => {
    draft[row.weekday] = {
      is_open: !!row.is_open,
      open_time: hhmm(row.open_time),
      close_time: hhmm(row.close_time),
      location_id: row.location_id || '',
    };
  });
  return draft;
};

const timeEcho = (value) => (value ? formatTime(value) : 'Not set yet');

const sameDay = (a, b) =>
  !!a &&
  !!b &&
  a.is_open === b.is_open &&
  (!a.is_open || (a.open_time === b.open_time && a.close_time === b.close_time && a.location_id === b.location_id));

const buildSettingsDraft = (cfg) => ({
  slot_interval_minutes: String(cfg?.slot_interval_minutes ?? 15),
  buffer_minutes: String(cfg?.buffer_minutes ?? 5),
  min_notice_hours: String(cfg?.min_notice_hours ?? 12),
  max_advance_days: String(cfg?.max_advance_days ?? 56),
});

/* ------------------------------------------------------------------ component */

const AvailabilityPanel = ({ showToast }) => {
  const notify = (type, message) => {
    if (typeof showToast === 'function') showToast(type, message);
  };

  const todayISO = toISODate(new Date());

  const [section, setSection] = useState('timeoff');
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  const [locations, setLocations] = useState([]);
  const [hours, setHours] = useState([]);
  const [hoursDraft, setHoursDraft] = useState({});
  const [savingDay, setSavingDay] = useState(null);
  const [dayError, setDayError] = useState(null); // { weekday, message }

  const [exceptions, setExceptions] = useState([]);
  const [timeOff, setTimeOff] = useState([]);

  const [settings, setSettings] = useState(null);
  const [settingsDraft, setSettingsDraft] = useState(buildSettingsDraft(null));
  const [savingSettings, setSavingSettings] = useState(false);

  const [pendingDelete, setPendingDelete] = useState(null); // { kind, id, label }
  const [deleting, setDeleting] = useState(false);

  /* time off modal */
  const [tofMode, setTofMode] = useState(null); // null | 'part' | 'days'
  const [tofStep, setTofStep] = useState('form');
  const [tofDate, setTofDate] = useState('');
  const [tofEndDate, setTofEndDate] = useState('');
  const [tofStart, setTofStart] = useState('09:00');
  const [tofEnd, setTofEnd] = useState('12:00');
  const [tofReason, setTofReason] = useState('');
  const [tofError, setTofError] = useState('');
  const [tofChecking, setTofChecking] = useState(false);
  const [tofCheckFailed, setTofCheckFailed] = useState(false);
  const [tofClashes, setTofClashes] = useState([]);
  const [tofTouchesOpen, setTofTouchesOpen] = useState(true);
  const [tofSaving, setTofSaving] = useState(false);
  const [tofSaved, setTofSaved] = useState(false);
  const tofSeq = useRef(0);

  /* one-off day modal */
  const [excOpen, setExcOpen] = useState(false);
  const [excStep, setExcStep] = useState('form');
  const [excDate, setExcDate] = useState('');
  const [excKind, setExcKind] = useState('closed'); // 'closed' | 'open'
  const [excFrom, setExcFrom] = useState('09:00');
  const [excTo, setExcTo] = useState('17:00');
  const [excLocation, setExcLocation] = useState('');
  const [excNote, setExcNote] = useState('');
  const [excError, setExcError] = useState('');
  const [excChecking, setExcChecking] = useState(false);
  const [excCheckFailed, setExcCheckFailed] = useState(false);
  const [excClashes, setExcClashes] = useState([]);
  const [excAdditive, setExcAdditive] = useState(false);
  const [excPrior, setExcPrior] = useState(null);
  const [excSaving, setExcSaving] = useState(false);
  const [excSaved, setExcSaved] = useState(false);
  const excSeq = useRef(0);

  /* ------------------------------------------------------------ data loading */

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const [locs, bh, exc, off, cfg] = await Promise.all([
        supabaseService.getLocations(),
        supabaseService.getBusinessHours(),
        supabaseService.getScheduleExceptions(),
        supabaseService.getTimeOff(),
        supabaseService.getBookingSettings(),
      ]);
      setLocations(locs || []);
      setHours(bh || []);
      setHoursDraft(buildHoursDraft(bh));
      setExceptions(exc || []);
      setTimeOff(off || []);
      setSettings(cfg || null);
      setSettingsDraft(buildSettingsDraft(cfg));
    } catch (err) {
      setLoadError(err.message || 'Could not load your availability.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Re-reads the week but keeps every other day exactly as she left it. Rebuilding
  // the whole draft would wipe unsaved edits on the other days and — worse — make
  // them look saved, because the "not saved yet" banner would clear with them.
  const reloadHours = async (savedWeekday) => {
    const bh = await supabaseService.getBusinessHours();
    setHours(bh || []);
    setHoursDraft((prev) => {
      const fresh = buildHoursDraft(bh);
      const next = { ...fresh };
      Object.keys(prev).forEach((key) => {
        if (Number(key) !== savedWeekday && prev[key]) next[key] = prev[key];
      });
      return next;
    });
  };

  const reloadTimeOff = async () => setTimeOff((await supabaseService.getTimeOff()) || []);
  const reloadExceptions = async () => setExceptions((await supabaseService.getScheduleExceptions()) || []);

  /* ------------------------------------------------- what a given date looks like */

  // The same resolution get_available_slots does: a one-off day change wins over
  // the weekly pattern. Used only to tell her whether a block actually takes any
  // bookable time away.
  const effectiveDay = (isoDate) => {
    const exc = exceptions.find((e) => e.exception_date === isoDate);
    if (exc) {
      if (exc.is_closed) return null;
      const open = hhmm(exc.open_time);
      const close = hhmm(exc.close_time);
      return open && close ? { open, close } : null;
    }
    const d = parseDate(isoDate);
    if (!d) return null;
    const bh = hours.find((h) => h.weekday === d.getDay());
    if (!bh || !bh.is_open) return null;
    const open = hhmm(bh.open_time);
    const close = hhmm(bh.close_time);
    return open && close ? { open, close } : null;
  };

  const blockTouchesOpenHours = (startDate, startTime, endDate, endTime) => {
    const blockStart = stampMinutes({ date: startDate, time: startTime });
    const blockEnd = stampMinutes({ date: endDate, time: endTime });
    let cursor = startDate;
    for (let i = 0; i < 400 && cursor <= endDate; i += 1) {
      const day = effectiveDay(cursor);
      if (day) {
        const dayStart = stampMinutes({ date: cursor, time: day.open });
        const dayEnd = stampMinutes({ date: cursor, time: day.close });
        if (blockStart < dayEnd && dayStart < blockEnd) return true;
      }
      cursor = nextISODate(cursor);
    }
    return false;
  };

  // Blocking time does not cancel anything, so every confirmed appointment the
  // block would sit on top of has to be shown before she commits.
  const findClashes = async (startDate, startTime, endDate, endTime) => {
    const blockStart = stampMinutes({ date: startDate, time: startTime });
    const blockEnd = stampMinutes({ date: endDate, time: endTime });
    // One day earlier than the block: an appointment booked on the previous date
    // can still run into it, and getBookingsInRange filters on the booking's date.
    const rows = await supabaseService.getBookingsInRange(
      toISODate(addDays(parseDate(startDate), -1)),
      endDate
    );
    return (rows || []).filter((b) => {
      const start = stampMinutes({ date: b.date, time: b.time });
      const end = start + (Number(b.total_duration) || 60);
      return start < blockEnd && blockStart < end;
    });
  };

  /* --------------------------------------------------------------- weekly hours */

  const setDay = (weekday, patch) => {
    setDayError(null);
    setHoursDraft((prev) => ({ ...prev, [weekday]: { ...prev[weekday], ...patch } }));
  };

  const savedDay = (weekday) => {
    const row = hours.find((h) => h.weekday === weekday);
    return row
      ? {
          is_open: !!row.is_open,
          open_time: hhmm(row.open_time),
          close_time: hhmm(row.close_time),
          location_id: row.location_id || '',
        }
      : null;
  };

  const dirtyDays = WEEK_ORDER.filter((wd) => hoursDraft[wd] && !sameDay(hoursDraft[wd], savedDay(wd)));

  const dayValidationError = (weekday) => {
    const d = hoursDraft[weekday];
    const name = DAY_NAMES[weekday];
    if (!d || !d.is_open) return '';
    if (!d.open_time || !d.close_time) {
      return `${name} is set to open but is missing a start or finish time. Add both, or set it to Closed.`;
    }
    if (timeToMinutes(d.open_time) >= timeToMinutes(d.close_time)) {
      return `${name} finishes at or before it starts. The finish time has to be later than the start time.`;
    }
    if (!d.location_id) {
      return `Choose where you are on ${name}. Clients are sent that address with their confirmation.`;
    }
    return '';
  };

  const saveDay = async (weekday) => {
    const problem = dayValidationError(weekday);
    if (problem) {
      setDayError({ weekday, message: problem });
      notify('error', problem);
      return;
    }
    const d = hoursDraft[weekday];
    setSavingDay(weekday);
    setDayError(null);
    try {
      // The location is kept on a closed day for the same reason the times are:
      // it is the only record of where that day used to run from.
      await supabaseService.updateBusinessHours(weekday, {
        is_open: d.is_open,
        open_time: d.open_time,
        close_time: d.close_time,
        location_id: d.location_id,
      });
      await reloadHours(weekday);
      notify(
        'success',
        d.is_open
          ? `${DAY_PLURAL[weekday]} are now ${formatTime(d.open_time)} – ${formatTime(d.close_time)}.`
          : `${DAY_PLURAL[weekday]} are now closed. Nobody can book one.`
      );
    } catch (err) {
      const message = err.message || `Could not save ${DAY_NAMES[weekday]}.`;
      setDayError({ weekday, message });
      notify('error', message);
    } finally {
      setSavingDay(null);
    }
  };

  const undoDay = (weekday) => {
    const saved = savedDay(weekday);
    if (saved) setHoursDraft((prev) => ({ ...prev, [weekday]: saved }));
    setDayError(null);
  };

  /* ------------------------------------------------------------------ time off */

  const openTimeOff = (mode) => {
    tofSeq.current += 1;
    setTofMode(mode);
    setTofStep('form');
    setTofDate(todayISO);
    setTofEndDate(todayISO);
    setTofStart('09:00');
    setTofEnd('12:00');
    setTofReason('');
    setTofError('');
    setTofClashes([]);
    setTofCheckFailed(false);
    setTofTouchesOpen(true);
    setTofSaved(false);
  };

  const closeTimeOff = () => {
    tofSeq.current += 1;
    setTofMode(null);
    setTofStep('form');
    setTofClashes([]);
    setTofSaved(false);
  };

  // Going back to edit invalidates whatever check is still in flight, so a slow
  // response for the old range cannot land on the new one's review screen.
  const backToTimeOffForm = () => {
    tofSeq.current += 1;
    setTofChecking(false);
    setTofStep('form');
  };

  // What actually gets written. Whole days run 00:00 on the first day through
  // 23:59 on the last, so the block covers every bookable minute of them.
  const tofRange = () =>
    tofMode === 'days'
      ? { startDate: tofDate, startTime: '00:00', endDate: tofEndDate, endTime: '23:59' }
      : { startDate: tofDate, startTime: tofStart, endDate: tofDate, endTime: tofEnd };

  const reviewTimeOff = async () => {
    const { startDate, startTime, endDate, endTime } = tofRange();

    if (!startDate || (tofMode === 'days' && !endDate)) {
      setTofError('Pick a date first.');
      return;
    }
    if (startDate < todayISO) {
      setTofError('That date has already gone. Blocking it changes nothing.');
      return;
    }
    if (tofMode === 'days' && endDate < startDate) {
      setTofError('The last day is before the first day. Swap them around.');
      return;
    }
    if (tofMode === 'part') {
      if (!startTime || !endTime) {
        setTofError('Both a start time and a finish time are needed.');
        return;
      }
      if (timeToMinutes(endTime) <= timeToMinutes(startTime)) {
        setTofError('The finish time has to be later than the start time.');
        return;
      }
    }

    setTofError('');
    setTofStep('review');
    setTofChecking(true);
    setTofCheckFailed(false);
    setTofClashes([]);
    setTofSaved(false);
    setTofTouchesOpen(blockTouchesOpenHours(startDate, startTime, endDate, endTime));

    const seq = ++tofSeq.current;
    try {
      const rows = await findClashes(startDate, startTime, endDate, endTime);
      if (seq !== tofSeq.current) return;
      setTofClashes(rows);
    } catch {
      if (seq === tofSeq.current) setTofCheckFailed(true);
    } finally {
      if (seq === tofSeq.current) setTofChecking(false);
    }
  };

  const commitTimeOff = async () => {
    const { startDate, startTime, endDate, endTime } = tofRange();
    const reviewedClashes = tofClashes;
    setTofSaving(true);

    try {
      // 'YYYY-MM-DDTHH:MM' with no zone suffix. Anything else — a Z, an offset,
      // toISOString() — moves the block 12 or 13 hours off what she chose.
      await supabaseService.addTimeOff({
        start_at: `${startDate}T${startTime}`,
        end_at: `${endDate}T${endTime}`,
        reason: tofReason.trim() || null,
      });
    } catch (err) {
      setTofSaving(false);
      setTofError(err.message || 'Could not save that block of time.');
      notify('error', err.message || 'Could not save that block of time.');
      return;
    }

    // Past this point the block exists. Everything below is reporting, and none of
    // it may claim the write failed — she would press the button again and end up
    // with the block saved twice.

    // The block now closes the window, so nothing further can be booked into it:
    // this answer is final in a way the review-step one never was. She may have
    // sat on that screen for twenty minutes while a client booked the very slot
    // it told her was clear.
    let clashes = reviewedClashes;
    let recheckFailed = false;
    try {
      clashes = await findClashes(startDate, startTime, endDate, endTime);
    } catch {
      recheckFailed = true;
    }

    let listStale = false;
    try {
      await reloadTimeOff();
    } catch {
      listStale = true;
    }

    setTofSaving(false);
    const tail = listStale ? ' The list below may not have refreshed.' : '';

    // Something arrived while she was reading. A toast cannot name the client, so
    // keep her on the review screen where it is spelled out.
    if (!recheckFailed && clashes.length > reviewedClashes.length) {
      setTofClashes(clashes);
      setTofSaved(true);
      notify('warning', `Time blocked out, but something was booked inside it while you were checking.${tail}`);
      return;
    }

    closeTimeOff();
    if (recheckFailed) {
      notify(
        'warning',
        `Time blocked out, but I could not re-check your diary against it. Look for yourself before you rely on it.${tail}`
      );
    } else if (clashes.length > 0) {
      notify(
        'warning',
        `Time blocked out. ${clashes.length} appointment${clashes.length === 1 ? ' is' : 's are'} still in your diary inside it — nobody has been told.${tail}`
      );
    } else {
      notify('success', `Time blocked out. Nobody can book over it.${tail}`);
    }
  };

  /* ------------------------------------------------------------- one-off days */

  const normalFor = (isoDate) => {
    const d = parseDate(isoDate);
    if (!d) return null;
    const bh = hours.find((h) => h.weekday === d.getDay());
    if (!bh) return null;
    return {
      weekday: bh.weekday,
      is_open: !!bh.is_open,
      open: hhmm(bh.open_time),
      close: hhmm(bh.close_time),
      location: bh.locations ? bh.locations.name : '',
    };
  };

  const existingException = (isoDate) => exceptions.find((e) => e.exception_date === isoDate) || null;

  const openException = () => {
    excSeq.current += 1;
    const normal = normalFor(todayISO);
    setExcOpen(true);
    setExcStep('form');
    setExcDate(todayISO);
    setExcKind(normal && normal.is_open ? 'closed' : 'open');
    setExcFrom('09:00');
    setExcTo('17:00');
    setExcLocation(locations.length === 1 ? locations[0].id : '');
    setExcNote('');
    setExcError('');
    setExcClashes([]);
    setExcCheckFailed(false);
    setExcAdditive(false);
    setExcPrior(null);
    setExcSaved(false);
  };

  const closeException = () => {
    excSeq.current += 1;
    setExcOpen(false);
    setExcStep('form');
    setExcClashes([]);
    setExcSaved(false);
  };

  const backToExceptionForm = () => {
    excSeq.current += 1;
    setExcChecking(false);
    setExcStep('form');
  };

  // An open exception REPLACES the day's hours — get_available_slots resolves the
  // day with coalesce(se.open_time, bh.open_time), it does not union the two. So
  // "working this day" on an already-open date with a narrower window subtracts
  // availability exactly like a block of time off, and the parts of the day the
  // new window no longer covers have to be checked for appointments.
  const exceptionPlan = () => {
    if (excKind === 'closed') return { additive: false, windows: [['00:00', '23:59']] };

    const prior = effectiveDay(excDate); // null when that date is currently closed
    if (!prior) return { additive: true, windows: [] };

    const widens =
      timeToMinutes(excFrom) <= timeToMinutes(prior.open) &&
      timeToMinutes(excTo) >= timeToMinutes(prior.close);
    if (widens) return { additive: true, windows: [], prior };

    const windows = [];
    if (timeToMinutes(excFrom) > 0) windows.push(['00:00', excFrom]);
    if (timeToMinutes(excTo) < 1439) windows.push([excTo, '23:59']);
    return { additive: windows.length === 0, windows, prior };
  };

  // One query for the whole date, then filtered locally against the parts of it
  // the change gives up.
  const clashesForPlan = async (plan) => {
    if (plan.additive || plan.windows.length === 0) return [];
    const all = await findClashes(excDate, '00:00', excDate, '23:59');
    if (excKind === 'closed') return all;
    return all.filter((b) => {
      const start = stampMinutes({ date: b.date, time: b.time });
      const end = start + (Number(b.total_duration) || 60);
      return plan.windows.some(
        ([from, to]) =>
          start < stampMinutes({ date: excDate, time: to }) &&
          stampMinutes({ date: excDate, time: from }) < end
      );
    });
  };

  const reviewException = async () => {
    if (!excDate) {
      setExcError('Pick a date first.');
      return;
    }
    if (excDate < todayISO) {
      setExcError('That date has already gone.');
      return;
    }
    if (excKind === 'open') {
      if (!excFrom || !excTo) {
        setExcError('An open day needs both a start and a finish time.');
        return;
      }
      if (timeToMinutes(excTo) <= timeToMinutes(excFrom)) {
        setExcError('The finish time has to be later than the start time.');
        return;
      }
      if (!excLocation) {
        setExcError('Choose where you will be. Clients are sent that address with their confirmation.');
        return;
      }
    }

    const plan = exceptionPlan();
    setExcError('');
    setExcStep('review');
    setExcClashes([]);
    setExcCheckFailed(false);
    setExcAdditive(plan.additive);
    // Held in state, not derived: once the exception is saved, effectiveDay()
    // returns the new window and the "replaces X – Y" line would describe itself.
    setExcPrior(plan.prior || null);
    setExcSaved(false);
    if (plan.additive) return;

    const seq = ++excSeq.current;
    setExcChecking(true);
    try {
      const rows = await clashesForPlan(plan);
      if (seq !== excSeq.current) return;
      setExcClashes(rows);
    } catch {
      if (seq === excSeq.current) setExcCheckFailed(true);
    } finally {
      if (seq === excSeq.current) setExcChecking(false);
    }
  };

  const commitException = async () => {
    // Worked out before the write: once the exception is saved, effectiveDay()
    // returns the new window and the plan would compute itself as harmless.
    const plan = exceptionPlan();
    const reviewedClashes = excClashes;
    const closing = excKind === 'closed';
    setExcSaving(true);

    try {
      await supabaseService.saveScheduleException({
        exception_date: excDate,
        is_closed: closing,
        open_time: excFrom,
        close_time: excTo,
        location_id: excLocation || null,
        note: excNote.trim() || null,
      });
    } catch (err) {
      setExcSaving(false);
      setExcError(err.message || 'Could not save that day change.');
      notify('error', err.message || 'Could not save that day change.');
      return;
    }

    let clashes = reviewedClashes;
    let recheckFailed = false;
    try {
      clashes = await clashesForPlan(plan);
    } catch {
      recheckFailed = true;
    }

    let listStale = false;
    try {
      await reloadExceptions();
    } catch {
      listStale = true;
    }

    setExcSaving(false);
    const tail = listStale ? ' The list below may not have refreshed.' : '';
    const what = closing
      ? `${formatDate(excDate)} is now closed`
      : `${formatDate(excDate)} is now ${formatTime(excFrom)} – ${formatTime(excTo)}`;

    if (!recheckFailed && clashes.length > reviewedClashes.length) {
      setExcClashes(clashes);
      setExcSaved(true);
      notify('warning', `${what}, but something was booked outside those hours while you were checking.${tail}`);
      return;
    }

    closeException();
    if (recheckFailed) {
      notify('warning', `${what}, but I could not re-check your diary against it. Look for yourself.${tail}`);
    } else if (clashes.length > 0) {
      notify(
        'warning',
        `${what}, but ${clashes.length} appointment${clashes.length === 1 ? ' is' : 's are'} still in your diary ${closing ? 'that day' : 'outside those hours'} — nobody has been told.${tail}`
      );
    } else {
      notify('success', `${what}.${tail}`);
    }
  };

  /* ----------------------------------------------------------------- deleting */

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    const { kind, id } = pendingDelete;
    const timeOffRow = kind === 'timeoff';
    setDeleting(true);

    try {
      await (timeOffRow
        ? supabaseService.deleteTimeOff(id)
        : supabaseService.deleteScheduleException(id));
    } catch (err) {
      setDeleting(false);
      notify('error', err.message || 'Could not remove that.');
      return;
    }

    // The row is gone. A failed refetch after this point must not be reported as a
    // failed delete — it only means the list on screen is behind.
    let listStale = false;
    try {
      await (timeOffRow ? reloadTimeOff() : reloadExceptions());
    } catch {
      listStale = true;
    }

    setDeleting(false);
    setPendingDelete(null);
    const tail = listStale ? ' The list below may not have refreshed.' : '';
    notify(
      'success',
      timeOffRow
        ? `Time off removed. That time is bookable again.${tail}`
        : `Day change removed. That date is back to its usual hours.${tail}`
    );
  };

  /* ------------------------------------------------------------ booking rules */

  const ruleNumber = (key) => {
    const n = parseInt(settingsDraft[key], 10);
    return Number.isFinite(n) ? n : null;
  };

  const settingsDirty =
    !!settings &&
    Object.keys(settingsDraft).some((k) => String(settings[k] ?? '') !== String(settingsDraft[k] ?? ''));

  const settingsProblem = () => {
    const interval = ruleNumber('slot_interval_minutes');
    const buffer = ruleNumber('buffer_minutes');
    const notice = ruleNumber('min_notice_hours');
    const advance = ruleNumber('max_advance_days');

    if (interval === null || interval < 5 || interval > 60) {
      return 'Start times have to be between 5 and 60 minutes apart.';
    }
    if (buffer === null || buffer < 0 || buffer > 120) {
      return 'The gap after each appointment has to be between 0 and 120 minutes.';
    }
    if (notice === null || notice < 0) {
      return 'Notice cannot be a negative number of hours.';
    }
    if (advance === null || advance < 1) {
      return 'The diary has to be open at least one day ahead, or nobody can book at all.';
    }
    // Notice longer than the whole open window leaves nothing bookable, and the
    // booking page would simply show no times with no explanation.
    if (notice >= advance * 24) {
      return `With ${notice} hours' notice and a diary open only ${advance} day${advance === 1 ? '' : 's'} ahead, there is no time left that anyone can book. Lower the notice or open the diary further ahead.`;
    }
    return '';
  };

  const saveSettings = async () => {
    const problem = settingsProblem();
    if (problem) {
      notify('error', problem);
      return;
    }
    setSavingSettings(true);
    try {
      const saved = await supabaseService.updateBookingSettings({
        slot_interval_minutes: ruleNumber('slot_interval_minutes'),
        buffer_minutes: ruleNumber('buffer_minutes'),
        min_notice_hours: ruleNumber('min_notice_hours'),
        max_advance_days: ruleNumber('max_advance_days'),
      });
      setSettings(saved);
      setSettingsDraft(buildSettingsDraft(saved));
      notify('success', 'Booking rules saved. Your booking page has changed straight away.');
    } catch (err) {
      notify('error', err.message || 'Could not save your booking rules.');
    } finally {
      setSavingSettings(false);
    }
  };

  /* ------------------------------------------------------------- render pieces */

  const renderClashes = (clashes) => (
    <ul className="av-clash-list">
      {clashes.map((b) => (
        <li key={b.id} className="av-clash">
          <span className="av-clash-name">{b.name || 'Client'}</span>
          <span className="av-clash-when">
            {formatDate(b.date)} · {formatTime(b.time)}
            {formatDuration(b.total_duration) ? ` · ${formatDuration(b.total_duration)}` : ''}
          </span>
          <span className="av-clash-what">{treatmentNames(b) || 'No treatments listed'}</span>
        </li>
      ))}
    </ul>
  );

  // scope 'inside'  — the change swallows time that is booked (a block, a closed day)
  // scope 'outside' — the change leaves booked time stranded beyond the new hours
  const renderCheckResult = (checking, failed, clashes, verb, scope = 'inside') => {
    if (checking) return <div className="av-loading">Checking your diary…</div>;
    if (failed) {
      return (
        <div className="av-alert warn">
          <AlertTriangle size={16} aria-hidden="true" />
          <span>
            I could not check your appointments for this. Look at your diary yourself before you {verb}.
          </span>
        </div>
      );
    }
    if (clashes.length === 0) {
      return (
        <div className="av-alert ok">
          <Check size={16} aria-hidden="true" />
          <span>
            {scope === 'inside'
              ? 'Nothing is booked in that time.'
              : 'Nothing in your diary falls outside the new hours.'}
          </span>
        </div>
      );
    }
    const one = clashes.length === 1;
    return (
      <div className="av-clash-box">
        <div className="av-alert error">
          <AlertTriangle size={16} aria-hidden="true" />
          <span>
            {scope === 'inside'
              ? `${clashes.length} confirmed appointment${one ? '' : 's'} already sit${one ? 's' : ''} inside this time.`
              : `${clashes.length} confirmed appointment${one ? '' : 's'} would be left outside the new hours.`}
          </span>
        </div>
        {renderClashes(clashes)}
        <p className="av-clash-note">
          This does not cancel {one ? 'it' : 'them'} and does not email or text anyone.{' '}
          {one ? 'That client' : 'Those clients'} will still turn up unless you contact them yourself.
        </p>
      </div>
    );
  };

  // Inline rather than a browser confirm() — a native dialog is easy to mis-tap
  // on a phone, and it cannot say what removing this actually frees up.
  const renderDeleteConfirm = (label) => (
    <div className="av-confirm">
      <span className="av-confirm-text">{label}</span>
      <div className="av-confirm-actions">
        <button type="button" className="av-btn-danger" onClick={confirmDelete} disabled={deleting}>
          {deleting ? 'Removing…' : 'Remove'}
        </button>
        <button type="button" className="av-btn-quiet" onClick={() => setPendingDelete(null)} disabled={deleting}>
          Keep
        </button>
      </div>
    </div>
  );

  const isDeleting = (kind, id) => pendingDelete && pendingDelete.kind === kind && pendingDelete.id === id;

  /* -------------------------------------------------------------------- gates */

  if (loading) {
    return (
      <div className="availability-panel">
        <div className="av-loading">Loading your availability…</div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="availability-panel">
        <div className="av-alert error">
          <AlertTriangle size={16} aria-hidden="true" />
          <span>{loadError}</span>
        </div>
        <button type="button" className="av-btn-primary" onClick={load}>
          <RefreshCw size={16} aria-hidden="true" /> Try again
        </button>
      </div>
    );
  }

  const openDayCount = WEEK_ORDER.filter((wd) => {
    const row = hours.find((h) => h.weekday === wd);
    return row && row.is_open;
  }).length;

  const interval = ruleNumber('slot_interval_minutes');
  const buffer = ruleNumber('buffer_minutes');
  const notice = ruleNumber('min_notice_hours');
  const advance = ruleNumber('max_advance_days');

  // A real opening time makes the slot-spacing example concrete rather than abstract.
  const firstOpen = hours.find((h) => h.is_open && h.open_time);
  const exampleOpenMins = firstOpen ? timeToMinutes(firstOpen.open_time) : 540;
  const exampleSlots =
    interval && interval > 0
      ? [0, 1, 2, 3]
          .map((i) => exampleOpenMins + i * interval)
          .map((m) => formatTime(`${pad(Math.floor(m / 60) % 24)}:${pad(m % 60)}`))
          .join(', ')
      : '';

  const tofRangeNow = tofMode ? tofRange() : null;
  const excNormal = excOpen ? normalFor(excDate) : null;
  const excExisting = excOpen ? existingException(excDate) : null;
  // Whether the change as currently filled in gives anything up. Drives the form's
  // button wording, so "Continue" never fronts a change that removes availability.
  const excFormPlan =
    excOpen && excStep === 'form' && (excKind === 'closed' || (excFrom && excTo)) ? exceptionPlan() : null;
  const excFormSubtracts = !!excFormPlan && !excFormPlan.additive;

  /* ------------------------------------------------------------------- render */

  return (
    <div className="availability-panel">
      <nav className="av-nav" aria-label="Availability sections">
        {SECTIONS.map((s) => {
          const Icon = s.icon;
          // Edits live in state while she is on another section, so the nav has
          // to carry the "you have not saved this" flag out of the section too.
          const unsaved =
            (s.key === 'hours' && dirtyDays.length > 0) || (s.key === 'rules' && settingsDirty);
          return (
            <button
              key={s.key}
              type="button"
              className={`av-nav-btn ${section === s.key ? 'active' : ''}`}
              onClick={() => setSection(s.key)}
              aria-pressed={section === s.key}
            >
              <Icon size={15} aria-hidden="true" />
              <span>{s.label}</span>
              {/* title= is a hover tooltip and there is no hover on a phone, so the
                  dot carries its meaning as text for anything that is not a mouse. */}
              {unsaved && (
                <>
                  <span className="av-nav-dot" aria-hidden="true" />
                  <span className="av-sr-only">has unsaved changes</span>
                </>
              )}
            </button>
          );
        })}
      </nav>

      {/* ============================================================ TIME OFF */}
      {section === 'timeoff' && (
        <section className="av-section">
          <header className="av-section-head">
            <h3>Time off</h3>
            <p>
              Time you are not available. Clients cannot book over it. It does not cancel anything already
              booked and it does not tell anyone — that is still up to you.
            </p>
          </header>

          <div className="av-add-grid">
            <button type="button" className="av-add-card" onClick={() => openTimeOff('part')}>
              <Clock size={20} aria-hidden="true" />
              <span className="av-add-title">Part of a day</span>
              <span className="av-add-sub">A few hours out — “Wednesday 2–4pm, dentist”</span>
            </button>
            <button type="button" className="av-add-card" onClick={() => openTimeOff('days')}>
              <CalendarOff size={20} aria-hidden="true" />
              <span className="av-add-title">Whole days</span>
              <span className="av-add-sub">A run of days blocked entirely — “14–21 September, holiday”</span>
            </button>
          </div>

          {timeOff.length === 0 ? (
            <div className="av-empty">
              <CalendarOff size={34} aria-hidden="true" />
              <h4>No time blocked out</h4>
              <p>Right now clients can book any time you are open.</p>
            </div>
          ) : (
            <ul className="av-list">
              {timeOff.map((row) => {
                const label = timeOffLabel(row);
                const state = timeOffState(row);
                return (
                  <li key={row.id} className={`av-row ${state.cls}`}>
                    {isDeleting('timeoff', row.id) ? (
                      renderDeleteConfirm('Unblock this time? Clients will be able to book it again.')
                    ) : (
                      <>
                        <div className="av-row-main">
                          <span className="av-row-title">{label.main}</span>
                          <span className="av-row-sub">{label.sub}</span>
                          {row.reason && <span className="av-row-reason">{row.reason}</span>}
                        </div>
                        <span className={`av-pill ${state.cls}`}>{state.label}</span>
                        <button
                          type="button"
                          className="av-row-delete"
                          aria-label={`Remove time off on ${label.main}`}
                          onClick={() => setPendingDelete({ kind: 'timeoff', id: row.id })}
                        >
                          <Trash2 size={17} aria-hidden="true" />
                        </button>
                      </>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      )}

      {/* ======================================================= ONE-OFF DAYS */}
      {section === 'exceptions' && (
        <section className="av-section">
          <header className="av-section-head">
            <h3>One-off day changes</h3>
            <p>
              Changes the shape of a single date — closing a day you normally work, or opening one you
              normally do not. It overrides your weekly hours for that date only.
            </p>
          </header>

          <div className="av-compare">
            <div className="av-compare-item">
              <CalendarX size={16} aria-hidden="true" />
              <span>
                <strong>Use a one-off day change</strong> for a whole date: “closed all Friday”, or “open this
                Saturday 10am–2pm”.
              </span>
            </div>
            <div className="av-compare-item">
              <CalendarClock size={16} aria-hidden="true" />
              <span>
                <strong>Use time off</strong> for a piece of a day inside your normal hours: “out 2–4pm on
                Wednesday”.
              </span>
            </div>
          </div>

          <button type="button" className="av-btn-primary" onClick={openException}>
            <Plus size={16} aria-hidden="true" /> Change a single day
          </button>

          {exceptions.length === 0 ? (
            <div className="av-empty">
              <CalendarPlus size={34} aria-hidden="true" />
              <h4>No day changes coming up</h4>
              <p>Every date is following your usual weekly hours.</p>
            </div>
          ) : (
            <ul className="av-list">
              {exceptions.map((row) => (
                <li key={row.id} className={`av-row ${row.is_closed ? 'closed' : 'special'}`}>
                  {isDeleting('exception', row.id) ? (
                    renderDeleteConfirm(
                      `Remove this change? ${formatDate(row.exception_date)} goes back to its usual hours.`
                    )
                  ) : (
                    <>
                      <div className="av-row-main">
                        <span className="av-row-title">{formatDate(row.exception_date)}</span>
                        <span className="av-row-sub">
                          {row.is_closed
                            ? 'Closed all day'
                            : `Open ${formatTime(row.open_time)} – ${formatTime(row.close_time)}${
                                row.locations ? ` at ${row.locations.name}` : ''
                              }`}
                        </span>
                        {row.note && <span className="av-row-reason">{row.note}</span>}
                      </div>
                      <span className={`av-pill ${row.is_closed ? 'closed' : 'special'}`}>
                        {row.is_closed ? 'Closed' : 'Open'}
                      </span>
                      <button
                        type="button"
                        className="av-row-delete"
                        aria-label={`Remove day change on ${formatDate(row.exception_date)}`}
                        onClick={() => setPendingDelete({ kind: 'exception', id: row.id })}
                      >
                        <Trash2 size={17} aria-hidden="true" />
                      </button>
                    </>
                  )}
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      {/* ======================================================== WEEKLY HOURS */}
      {section === 'hours' && (
        <section className="av-section">
          <header className="av-section-head">
            <h3>Weekly hours</h3>
            <p>Your usual week. Every date follows this unless a one-off day change says otherwise.</p>
          </header>

          {openDayCount === 0 && (
            <div className="av-alert error">
              <AlertTriangle size={16} aria-hidden="true" />
              <span>
                Every day of the week is closed, so nobody can book anything at all. Open at least one day.
              </span>
            </div>
          )}

          {dirtyDays.length > 0 && (
            <div className="av-alert warn">
              <AlertTriangle size={16} aria-hidden="true" />
              <span>
                Not saved yet: {dirtyDays.map((wd) => DAY_NAMES[wd]).join(', ')}. Nothing has changed for
                clients until you save each one.
              </span>
            </div>
          )}

          <div className="av-days">
            {WEEK_ORDER.map((wd) => {
              const draft = hoursDraft[wd];
              if (!draft) {
                return (
                  <div key={wd} className="av-day-card missing">
                    <span className="av-day-name">{DAY_NAMES[wd]}</span>
                    <p className="av-day-note">
                      This day is missing from your opening hours, so it is treated as closed.
                    </p>
                  </div>
                );
              }
              const dirty = !sameDay(draft, savedDay(wd));
              const saved = hours.find((h) => h.weekday === wd);
              const err = dayError && dayError.weekday === wd ? dayError.message : '';

              return (
                <div key={wd} className={`av-day-card ${draft.is_open ? 'open' : 'shut'} ${dirty ? 'dirty' : ''}`}>
                  <div className="av-day-head">
                    <span className="av-day-name">{DAY_NAMES[wd]}</span>
                    <div className="av-switch">
                      <button
                        type="button"
                        className={draft.is_open ? 'on' : ''}
                        onClick={() => setDay(wd, { is_open: true })}
                        aria-pressed={draft.is_open}
                      >
                        Open
                      </button>
                      <button
                        type="button"
                        className={!draft.is_open ? 'on' : ''}
                        onClick={() => setDay(wd, { is_open: false })}
                        aria-pressed={!draft.is_open}
                      >
                        Closed
                      </button>
                    </div>
                  </div>

                  {draft.is_open ? (
                    <>
                      <div className="av-day-fields">
                        <label className="av-field">
                          <span className="av-field-label">Starts</span>
                          <input
                            type="time"
                            value={draft.open_time}
                            onChange={(e) => setDay(wd, { open_time: e.target.value })}
                          />
                          <span className="av-field-echo">{timeEcho(draft.open_time)}</span>
                        </label>
                        <label className="av-field">
                          <span className="av-field-label">Finishes</span>
                          <input
                            type="time"
                            value={draft.close_time}
                            onChange={(e) => setDay(wd, { close_time: e.target.value })}
                          />
                          <span className="av-field-echo">{timeEcho(draft.close_time)}</span>
                        </label>
                        <label className="av-field wide">
                          <span className="av-field-label">Where you are</span>
                          <select
                            value={draft.location_id}
                            onChange={(e) => setDay(wd, { location_id: e.target.value })}
                          >
                            <option value="">Choose a place…</option>
                            {locations.map((l) => (
                              <option key={l.id} value={l.id}>
                                {l.name}
                              </option>
                            ))}
                          </select>
                        </label>
                      </div>
                      {(!draft.open_time || !draft.close_time) && (
                        <p className="av-day-note">
                          This day has never had hours saved. Fill both times in before saving it.
                        </p>
                      )}
                      {(() => {
                        const loc = locations.find((l) => l.id === draft.location_id);
                        return loc ? (
                          <p className="av-day-note">
                            <MapPin size={13} aria-hidden="true" /> {loc.address}
                          </p>
                        ) : null;
                      })()}
                    </>
                  ) : (
                    <p className="av-day-note">Nobody can book a {DAY_NAMES[wd]}.</p>
                  )}

                  {err && (
                    <div className="av-alert error tight">
                      <AlertTriangle size={15} aria-hidden="true" />
                      <span>{err}</span>
                    </div>
                  )}

                  {dirty ? (
                    <div className="av-day-actions">
                      <button
                        type="button"
                        className="av-btn-primary small"
                        onClick={() => saveDay(wd)}
                        disabled={savingDay === wd}
                      >
                        {savingDay === wd ? 'Saving…' : `Save ${DAY_NAMES[wd]}`}
                      </button>
                      <button type="button" className="av-btn-quiet" onClick={() => undoDay(wd)} disabled={savingDay === wd}>
                        Undo
                      </button>
                    </div>
                  ) : (
                    <p className="av-day-saved">
                      {saved && saved.is_open
                        ? `Clients can book ${DAY_PLURAL[wd]} ${formatTime(saved.open_time)} – ${formatTime(saved.close_time)}${
                            saved.locations ? ` at ${saved.locations.name}` : ''
                          }.`
                        : /* Closing a day no longer wipes its hours, so say they are still
                             on file — otherwise reopening looks like starting from scratch. */
                          `${DAY_PLURAL[wd]} are closed.${
                            saved && saved.open_time && saved.close_time
                              ? ` Your ${formatTime(saved.open_time)} – ${formatTime(saved.close_time)} hours are kept for when you open them again.`
                              : ''
                          }`}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* ======================================================= BOOKING RULES */}
      {section === 'rules' && (
        <section className="av-section">
          <header className="av-section-head">
            <h3>Booking rules</h3>
            <p>
              These decide how many times a client is offered inside the hours you are open. Change one and
              your booking page changes straight away.
            </p>
          </header>

          {!settings ? (
            <div className="av-alert error">
              <AlertTriangle size={16} aria-hidden="true" />
              <span>Your booking rules are missing from the database, so they cannot be edited here.</span>
            </div>
          ) : (
            <>
              <div className="av-rule">
                <label className="av-field">
                  <span className="av-rule-title">How late someone can book</span>
                  <div className="av-rule-input">
                    <input
                      type="number"
                      inputMode="numeric"
                      min="0"
                      max="336"
                      value={settingsDraft.min_notice_hours}
                      onChange={(e) =>
                        setSettingsDraft((p) => ({ ...p, min_notice_hours: e.target.value }))
                      }
                    />
                    <span className="av-rule-unit">hours' notice</span>
                  </div>
                </label>
                <p className="av-rule-effect">
                  {notice === null
                    ? 'Enter a number of hours.'
                    : notice === 0
                      ? 'Anyone can book a time starting in a few minutes. You would get no warning at all.'
                      : `Nobody can book anything starting in the next ${notice} hour${notice === 1 ? '' : 's'}. Right now the earliest a client could book is ${formatNowPlusHours(notice)}.`}
                </p>
                {notice !== null && notice > 72 && (
                  <p className="av-rule-warn">
                    That is more than three days. Anyone wanting an appointment sooner has to ring you.
                  </p>
                )}
              </div>

              <div className="av-rule">
                <label className="av-field">
                  <span className="av-rule-title">How far ahead the diary is open</span>
                  <div className="av-rule-input">
                    <input
                      type="number"
                      inputMode="numeric"
                      min="1"
                      max="365"
                      value={settingsDraft.max_advance_days}
                      onChange={(e) => setSettingsDraft((p) => ({ ...p, max_advance_days: e.target.value }))}
                    />
                    <span className="av-rule-unit">days ahead</span>
                  </div>
                </label>
                <p className="av-rule-effect">
                  {advance === null
                    ? 'Enter a number of days.'
                    : `Clients see nothing past ${formatDate(toISODate(addDays(new Date(), advance)))}. That is the last day anyone can book.`}
                </p>
              </div>

              <div className="av-rule">
                <label className="av-field">
                  <span className="av-rule-title">How far apart the start times are</span>
                  <div className="av-rule-input">
                    <select
                      value={settingsDraft.slot_interval_minutes}
                      onChange={(e) =>
                        setSettingsDraft((p) => ({ ...p, slot_interval_minutes: e.target.value }))
                      }
                    >
                      {SLOT_INTERVALS.map((v) => (
                        <option key={v} value={String(v)}>
                          {v} minutes
                        </option>
                      ))}
                    </select>
                  </div>
                </label>
                <p className="av-rule-effect">
                  {exampleSlots
                    ? `Start times are offered as ${exampleSlots}, and so on.`
                    : 'Start times are spaced by this many minutes.'}{' '}
                  A bigger number offers fewer, tidier times; a smaller number offers more and fills gaps, but
                  can leave you with odd short breaks.
                </p>
              </div>

              <div className="av-rule">
                <label className="av-field">
                  <span className="av-rule-title">Gap left after every appointment</span>
                  <div className="av-rule-input">
                    <select
                      value={settingsDraft.buffer_minutes}
                      onChange={(e) => setSettingsDraft((p) => ({ ...p, buffer_minutes: e.target.value }))}
                    >
                      {BUFFERS.map((v) => (
                        <option key={v} value={String(v)}>
                          {v === 0 ? 'No gap' : `${v} minutes`}
                        </option>
                      ))}
                    </select>
                  </div>
                </label>
                <p className="av-rule-effect">
                  {buffer === 0
                    ? 'One client can be booked to start the moment the last one finishes, with no time to clean down or write notes.'
                    : `An appointment finishing at 10:35 means the next client cannot start before ${formatTime(
                        `${pad(Math.floor((635 + (buffer || 0)) / 60))}:${pad((635 + (buffer || 0)) % 60)}`
                      )}. Every extra minute here removes bookable time from your day.`}
                </p>
              </div>

              {settingsDirty && settingsProblem() && (
                <div className="av-alert error">
                  <AlertTriangle size={16} aria-hidden="true" />
                  <span>{settingsProblem()}</span>
                </div>
              )}

              <div className="av-rule-actions">
                <button
                  type="button"
                  className="av-btn-primary"
                  onClick={saveSettings}
                  disabled={!settingsDirty || savingSettings || !!settingsProblem()}
                >
                  {savingSettings ? 'Saving…' : 'Save booking rules'}
                </button>
                {settingsDirty && (
                  <button
                    type="button"
                    className="av-btn-quiet"
                    onClick={() => setSettingsDraft(buildSettingsDraft(settings))}
                    disabled={savingSettings}
                  >
                    Undo
                  </button>
                )}
              </div>
              {!settingsDirty && <p className="av-rule-saved">Saved. Nothing to change.</p>}
            </>
          )}
        </section>
      )}

      {/* ================================================== TIME OFF MODAL */}
      {tofMode && (
        <div className="modal-overlay av-overlay" onClick={closeTimeOff}>
          <div className="modal-content av-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{tofMode === 'part' ? 'Block part of a day' : 'Block whole days'}</h2>
              <button type="button" className="modal-close" onClick={closeTimeOff} aria-label="Close">
                ×
              </button>
            </div>

            <div className="modal-body av-modal-body">
              {tofStep === 'form' && (
                <>
                  {tofMode === 'part' ? (
                    <>
                      <label className="av-field">
                        <span className="av-field-label">Which day</span>
                        <input
                          type="date"
                          min={todayISO}
                          value={tofDate}
                          onChange={(e) => setTofDate(e.target.value)}
                        />
                        <span className="av-field-echo">{formatDate(tofDate)}</span>
                      </label>
                      <div className="av-field-pair">
                        <label className="av-field">
                          <span className="av-field-label">From</span>
                          <input type="time" value={tofStart} onChange={(e) => setTofStart(e.target.value)} />
                          <span className="av-field-echo">{formatTime(tofStart)}</span>
                        </label>
                        <label className="av-field">
                          <span className="av-field-label">Until</span>
                          <input type="time" value={tofEnd} onChange={(e) => setTofEnd(e.target.value)} />
                          <span className="av-field-echo">{formatTime(tofEnd)}</span>
                        </label>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="av-field-pair">
                        <label className="av-field">
                          <span className="av-field-label">First day off</span>
                          <input
                            type="date"
                            min={todayISO}
                            value={tofDate}
                            onChange={(e) => {
                              setTofDate(e.target.value);
                              if (tofEndDate < e.target.value) setTofEndDate(e.target.value);
                            }}
                          />
                          <span className="av-field-echo">{formatDate(tofDate)}</span>
                        </label>
                        <label className="av-field">
                          <span className="av-field-label">Last day off</span>
                          <input
                            type="date"
                            min={tofDate || todayISO}
                            value={tofEndDate}
                            onChange={(e) => setTofEndDate(e.target.value)}
                          />
                          <span className="av-field-echo">{formatDate(tofEndDate)}</span>
                        </label>
                      </div>
                      <p className="av-hint">
                        Both days are blocked in full, and so is everything between them.
                      </p>
                    </>
                  )}

                  <label className="av-field">
                    <span className="av-field-label">What is it for (optional)</span>
                    <input
                      type="text"
                      maxLength={120}
                      placeholder={tofMode === 'part' ? 'Dentist' : 'Holiday'}
                      value={tofReason}
                      onChange={(e) => setTofReason(e.target.value)}
                    />
                  </label>
                  <p className="av-hint">Only you see this. Clients are never shown a reason.</p>

                  {tofError && (
                    <div className="av-alert error">
                      <AlertTriangle size={16} aria-hidden="true" />
                      <span>{tofError}</span>
                    </div>
                  )}
                </>
              )}

              {tofStep === 'review' && tofRangeNow && (
                <>
                  <div className="av-review">
                    <span className="av-review-label">{tofSaved ? 'Blocked' : 'About to block'}</span>
                    <span className="av-review-main">
                      {tofMode === 'part'
                        ? `${formatDate(tofRangeNow.startDate)}, ${formatTime(tofRangeNow.startTime)} – ${formatTime(tofRangeNow.endTime)}`
                        : tofRangeNow.startDate === tofRangeNow.endDate
                          ? `All day ${formatDate(tofRangeNow.startDate)}`
                          : `${formatDateNoYear(tofRangeNow.startDate)} – ${formatDate(tofRangeNow.endDate)}, all day`}
                    </span>
                    {tofReason.trim() && <span className="av-review-sub">{tofReason.trim()}</span>}
                  </div>

                  {tofSaved && (
                    <div className="av-alert warn">
                      <AlertTriangle size={16} aria-hidden="true" />
                      <span>
                        The time is blocked, but this was booked while you were on this screen. Nothing
                        further can be booked into it now.
                      </span>
                    </div>
                  )}

                  {renderCheckResult(tofChecking, tofCheckFailed, tofClashes, 'block this time')}

                  {!tofChecking && !tofTouchesOpen && !tofSaved && (
                    <div className="av-alert info">
                      <Info size={16} aria-hidden="true" />
                      <span>
                        You are already closed for all of that, so blocking it changes nothing for clients. It
                        is harmless to save.
                      </span>
                    </div>
                  )}

                  {tofError && (
                    <div className="av-alert error">
                      <AlertTriangle size={16} aria-hidden="true" />
                      <span>{tofError}</span>
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="av-modal-foot">
              {tofStep === 'form' ? (
                <>
                  <button type="button" className="av-btn-primary" onClick={reviewTimeOff}>
                    Check my diary
                  </button>
                  <button type="button" className="av-btn-quiet wide" onClick={closeTimeOff}>
                    Cancel
                  </button>
                </>
              ) : tofSaved ? (
                <button type="button" className="av-btn-primary" onClick={closeTimeOff}>
                  Done
                </button>
              ) : (
                <>
                  <button
                    type="button"
                    className={`av-btn-primary ${tofClashes.length > 0 ? 'danger' : ''}`}
                    onClick={commitTimeOff}
                    disabled={tofChecking || tofSaving}
                  >
                    {tofSaving ? 'Blocking…' : tofClashes.length > 0 ? 'Block it anyway' : 'Block this time'}
                  </button>
                  <button
                    type="button"
                    className="av-btn-quiet wide"
                    onClick={backToTimeOffForm}
                    disabled={tofSaving}
                  >
                    Back
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ================================================ ONE-OFF DAY MODAL */}
      {excOpen && (
        <div className="modal-overlay av-overlay" onClick={closeException}>
          <div className="modal-content av-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Change a single day</h2>
              <button type="button" className="modal-close" onClick={closeException} aria-label="Close">
                ×
              </button>
            </div>

            <div className="modal-body av-modal-body">
              {excStep === 'form' && (
                <>
                  <label className="av-field">
                    <span className="av-field-label">Which date</span>
                    <input
                      type="date"
                      min={todayISO}
                      value={excDate}
                      onChange={(e) => setExcDate(e.target.value)}
                    />
                    <span className="av-field-echo">{formatDate(excDate)}</span>
                  </label>

                  {excNormal && (
                    <p className="av-hint">
                      {excNormal.is_open
                        ? `${DAY_PLURAL[excNormal.weekday]} are normally ${formatTime(excNormal.open)} – ${formatTime(excNormal.close)}${excNormal.location ? ` at ${excNormal.location}` : ''}.`
                        : `${DAY_PLURAL[excNormal.weekday]} are normally closed.`}
                    </p>
                  )}

                  {excExisting && (
                    <div className="av-alert warn">
                      <AlertTriangle size={16} aria-hidden="true" />
                      <span>
                        You already have a change saved for that date (
                        {excExisting.is_closed
                          ? 'closed all day'
                          : `open ${formatTime(excExisting.open_time)} – ${formatTime(excExisting.close_time)}`}
                        ). Saving replaces it.
                      </span>
                    </div>
                  )}

                  <div className="av-choice">
                    <button
                      type="button"
                      className={`av-choice-btn ${excKind === 'closed' ? 'active' : ''}`}
                      onClick={() => setExcKind('closed')}
                      aria-pressed={excKind === 'closed'}
                    >
                      <CalendarX size={18} aria-hidden="true" />
                      <span className="av-choice-title">Closed all day</span>
                      <span className="av-choice-sub">Nobody can book this date at all</span>
                    </button>
                    <button
                      type="button"
                      className={`av-choice-btn ${excKind === 'open' ? 'active' : ''}`}
                      onClick={() => setExcKind('open')}
                      aria-pressed={excKind === 'open'}
                    >
                      <CalendarPlus size={18} aria-hidden="true" />
                      <span className="av-choice-title">Working this day</span>
                      <span className="av-choice-sub">Set hours and a place for this date only</span>
                    </button>
                  </div>

                  {excKind === 'closed' && excNormal && !excNormal.is_open && (
                    <div className="av-alert info">
                      <Info size={16} aria-hidden="true" />
                      <span>
                        You are already closed on {DAY_PLURAL[excNormal.weekday].toLowerCase()}, so this
                        changes nothing.
                      </span>
                    </div>
                  )}

                  {excKind === 'open' && excFormPlan && excFormPlan.prior && (
                    <div className={`av-alert ${excFormSubtracts ? 'warn' : 'info'}`}>
                      {excFormSubtracts ? (
                        <AlertTriangle size={16} aria-hidden="true" />
                      ) : (
                        <Info size={16} aria-hidden="true" />
                      )}
                      <span>
                        That date is already open {formatTime(excFormPlan.prior.open)} –{' '}
                        {formatTime(excFormPlan.prior.close)}. These hours <strong>replace</strong> those, they
                        are not added to them
                        {excFormSubtracts
                          ? ` — so anything outside ${formatTime(excFrom)} – ${formatTime(excTo)} stops being bookable.`
                          : '.'}
                      </span>
                    </div>
                  )}

                  {excKind === 'open' && (
                    <>
                      <div className="av-field-pair">
                        <label className="av-field">
                          <span className="av-field-label">Starts</span>
                          <input type="time" value={excFrom} onChange={(e) => setExcFrom(e.target.value)} />
                          <span className="av-field-echo">{formatTime(excFrom)}</span>
                        </label>
                        <label className="av-field">
                          <span className="av-field-label">Finishes</span>
                          <input type="time" value={excTo} onChange={(e) => setExcTo(e.target.value)} />
                          <span className="av-field-echo">{formatTime(excTo)}</span>
                        </label>
                      </div>
                      <label className="av-field">
                        <span className="av-field-label">Where you are</span>
                        <select value={excLocation} onChange={(e) => setExcLocation(e.target.value)}>
                          <option value="">Choose a place…</option>
                          {locations.map((l) => (
                            <option key={l.id} value={l.id}>
                              {l.name}
                            </option>
                          ))}
                        </select>
                      </label>
                    </>
                  )}

                  <label className="av-field">
                    <span className="av-field-label">Note (optional)</span>
                    <input
                      type="text"
                      maxLength={120}
                      placeholder={excKind === 'closed' ? 'Public holiday' : 'Catch-up Saturday'}
                      value={excNote}
                      onChange={(e) => setExcNote(e.target.value)}
                    />
                  </label>
                  <p className="av-hint">Only you see this.</p>

                  {excError && (
                    <div className="av-alert error">
                      <AlertTriangle size={16} aria-hidden="true" />
                      <span>{excError}</span>
                    </div>
                  )}
                </>
              )}

              {excStep === 'review' && (
                <>
                  <div className="av-review">
                    <span className="av-review-label">{excSaved ? 'Saved' : 'About to save'}</span>
                    <span className="av-review-main">
                      {excKind === 'closed'
                        ? `${formatDate(excDate)} — closed all day`
                        : `${formatDate(excDate)} — open ${formatTime(excFrom)} – ${formatTime(excTo)}`}
                    </span>
                    {excKind === 'open' && (
                      <span className="av-review-sub">
                        {(locations.find((l) => l.id === excLocation) || {}).name || 'No place chosen'}
                      </span>
                    )}
                    {excNote.trim() && <span className="av-review-sub">{excNote.trim()}</span>}
                  </div>

                  {excSaved && (
                    <div className="av-alert warn">
                      <AlertTriangle size={16} aria-hidden="true" />
                      <span>
                        The day is saved, but this was booked while you were on this screen.
                      </span>
                    </div>
                  )}

                  {/* These hours replace the day's usual ones rather than adding to
                      them, so a narrower window has to be described as a loss. */}
                  {excKind === 'open' && !excAdditive && excPrior && !excSaved && (
                    <div className="av-alert warn">
                      <AlertTriangle size={16} aria-hidden="true" />
                      <span>
                        This replaces {formatTime(excPrior.open)} – {formatTime(excPrior.close)} on that date
                        rather than adding to it. Anything outside {formatTime(excFrom)} – {formatTime(excTo)}{' '}
                        stops being bookable.
                      </span>
                    </div>
                  )}

                  {excAdditive ? (
                    <div className="av-alert ok">
                      <Check size={16} aria-hidden="true" />
                      <span>
                        {excPrior
                          ? 'These hours cover everything the day already had, so nothing is taken away.'
                          : 'That date is currently closed, so this only adds times. Nothing in your diary is affected.'}
                      </span>
                    </div>
                  ) : (
                    renderCheckResult(
                      excChecking,
                      excCheckFailed,
                      excClashes,
                      excKind === 'closed' ? 'close this day' : 'change these hours',
                      excKind === 'closed' ? 'inside' : 'outside'
                    )
                  )}

                  {excError && (
                    <div className="av-alert error">
                      <AlertTriangle size={16} aria-hidden="true" />
                      <span>{excError}</span>
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="av-modal-foot">
              {excStep === 'form' ? (
                <>
                  <button type="button" className="av-btn-primary" onClick={reviewException}>
                    {excFormSubtracts ? 'Check my diary' : 'Continue'}
                  </button>
                  <button type="button" className="av-btn-quiet wide" onClick={closeException}>
                    Cancel
                  </button>
                </>
              ) : excSaved ? (
                <button type="button" className="av-btn-primary" onClick={closeException}>
                  Done
                </button>
              ) : (
                <>
                  <button
                    type="button"
                    className={`av-btn-primary ${excClashes.length > 0 ? 'danger' : ''}`}
                    onClick={commitException}
                    disabled={excChecking || excSaving}
                  >
                    {excSaving
                      ? 'Saving…'
                      : excClashes.length > 0
                        ? excKind === 'closed'
                          ? 'Close it anyway'
                          : 'Save it anyway'
                        : excKind === 'closed'
                          ? 'Close this day'
                          : 'Save these hours'}
                  </button>
                  <button
                    type="button"
                    className="av-btn-quiet wide"
                    onClick={backToExceptionForm}
                    disabled={excSaving}
                  >
                    Back
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AvailabilityPanel;
